import React, { useState, useMemo, useEffect, useRef, useCallback } from "react"
import {
  Phone, Play, Pause, Activity, ChevronLeft, ChevronRight,
  Headphones, Plus, X, Loader2, Upload, CheckCircle2,
  AlertCircle, FileText, ArrowLeft, ArrowRight, Settings,
  Clock, RefreshCw, Flag, Users, Edit, Trash2, Pencil, Zap, Layers
} from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Badge } from "../ui/badge"
import { toast } from "sonner"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { ScrollArea } from "../ui/scroll-area"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog"
import API from "../api/api01"

// ─── Types ───────────────────────────────────────────────────────────────────
type Contact = {
  id: string; campaign_id: string; name: string; phone: string
  status: string; attempts: number; serial_number?: number; recording_url?: string
}
type Campaign = {
  id: string; name: string; description?: string; priority: string
  max_retries: number; call_from: string; call_to: string
  total_contacts: number; completed_contacts: number; status: string; created_at?: string
  ivr_rec_name?: string
}
type CsvRow = { name: string; phone: string;[key: string]: string }

const STEPS = [
  { id: 1, label: "Campaign Info", icon: Settings },
  { id: 2, label: "Upload Contacts", icon: Users },
  { id: 3, label: "Review & Create", icon: CheckCircle2 },
]
const PRIORITIES = [
  { value: "high", label: "High", color: "bg-rose-500 shadow-rose-500/20" },
  { value: "medium", label: "Medium", color: "bg-amber-500 shadow-amber-500/20" },
  { value: "low", label: "Low", color: "bg-emerald-500 shadow-emerald-500/20" },
]



// ─── Main Component ───────────────────────────────────────────────────────────
export default function AutoDialer() {

  // Dialer session state
  const [elapsed, setElapsed] = useState(0)
  const [currentTime, setCurrentTime] = useState(new Date())

  // Clock effect
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  // Data
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("")
  const [contacts, setContacts] = useState<Contact[]>([])

  // Wizard
  const [showWizard, setShowWizard] = useState(false)
  const [wizardStep, setWizardStep] = useState(1)
  const [creating, setCreating] = useState(false)

  // Edit
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null)
  const [editName, setEditName] = useState("")
  const [editDesc, setEditDesc] = useState("")
  const [editPriority, setEditPriority] = useState<"high" | "medium" | "low">("medium")
  const [editRetries, setEditRetries] = useState(3)
  const [editFrom, setEditFrom] = useState("09:00")
  const [editTo, setEditTo] = useState("18:00")
  const [editIvrName, setEditIvrName] = useState("")
  const [updating, setUpdating] = useState(false)

  const [deletingCampaign, setDeletingCampaign] = useState<Campaign | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Relaunch
  const [relaunchingCampaign, setRelaunchingCampaign] = useState<Campaign | null>(null)
  const [isRelaunching, setIsRelaunching] = useState(false)

  // Step 1
  const [campaignName, setCampaignName] = useState("")
  const [description, setDescription] = useState("")
  const [priority, setPriority] = useState<"high" | "medium" | "low">("medium")
  const [maxRetries, setMaxRetries] = useState(3)
  const [callFrom, setCallFrom] = useState("09:00")
  const [callTo, setCallTo] = useState("18:00")
  const [ivrName, setIvrName] = useState("XeroAlpha")
  const [assistants, setAssistants] = useState<{id: string, name: string}[]>([])

  // Step 2
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvRows, setCsvRows] = useState<CsvRow[]>([])
  const [csvError, setCsvError] = useState<string>("")
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)

  // Table
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("All")
  const [page, setPage] = useState(1)
  const [player, setPlayer] = useState<string | null>(null)
  const [view, setView] = useState<"contacts" | "campaigns">("campaigns")
  const pageSize = 8

  // Derived state
  const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId)
  const dialerRunning = campaigns.some(c => c.status === "dispatched")
  const progress = (view === "contacts" && selectedCampaign)
    ? Math.floor((selectedCampaign.completed_contacts / (selectedCampaign.total_contacts || 1)) * 100) : 0

  const derivedStats = useMemo(() => {
    const isSelected = selectedCampaignId && view === "contacts"
    if (!isSelected) return { total: 0, completed: 0, initiated: 0, dialing: 0, failed: 0, queued: 0, successRate: 0 }

    const total = contacts.length;
    const completed = contacts.filter(c => c.status === "Completed").length;
    const failed = contacts.filter(c => c.status === "Failed").length;
    const successRate = total > 0 ? Math.round((completed / (completed + failed || 1)) * 100) : 0;

    return {
      total,
      completed,
      initiated: contacts.filter(c => c.status === "Initiated").length,
      dialing: contacts.filter(c => c.status === "Dialing").length,
      failed,
      queued: contacts.filter(c => c.status === "Queued").length,
      successRate
    }
  }, [contacts, selectedCampaignId, view])

  // Global campaign timer
  useEffect(() => {
    let interval: any;
    if (selectedCampaign?.status === "dispatched") {
      interval = setInterval(() => {
        setElapsed(e => e + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedCampaign?.status, player]);

  // ── Timer — driven by audio timeupdate ───────────────────────────────────
  // elapsed is updated via onTimeUpdate on the <audio> element (see player modal)

  // ── Load ───────────────────────────────────────────────────────────────────
  useEffect(() => { 
    fetchCampaigns()
    fetchAssistants()
  }, [])
  useEffect(() => { if (selectedCampaignId) fetchContacts(selectedCampaignId) }, [selectedCampaignId])

  const fetchAssistants = async () => {
    try {
      const res = await API.get("/auto-dialer/assistants")
      const data = Array.isArray(res.data) ? res.data : (res.data.data || [])
      setAssistants(data)
    } catch (e) {
      console.error("Failed to fetch assistants", e)
    }
  }

  // Auto-refresh interval (30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCampaigns(true);
      if (selectedCampaignId) fetchContacts(selectedCampaignId, true);
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedCampaignId]);

  const fetchCampaigns = async (silent = false) => {
    try {
      const response = await API.get("/auto-dialer/campaigns")
      setCampaigns(response.data || [])
    } catch {
      if (!silent) toast.error("Failed to load campaigns")
    }
  }
  const fetchContacts = async (id: string, silent = false) => {
    try {
      const response = await API.get(`/auto-dialer/campaigns/${id}/contacts`)
      setContacts(response.data || [])
    }
    catch {
      if (!silent) toast.error("Failed to load contacts")
    }
  }


  // ── CSV ────────────────────────────────────────────────────────────────────
  const parseCSV = useCallback((file: File) => {
    setCsvError("")
    const reader = new FileReader()
    reader.onload = e => {
      const content = e.target?.result as string;
      if (!content) { setCsvError("File is empty"); return }

      const lines = content.trim().split(/\r?\n/)
      if (lines.length < 2) { setCsvError("CSV must have a header and at least one contact"); return }

      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/^"|"$/g, ""))
      if (!headers.includes("name") || !headers.includes("phone")) {
        setCsvError(`Missing columns. Need: 'name' and 'phone'. Found: ${headers.join(", ")}`)
        setCsvRows([]); return
      }

      const rows: CsvRow[] = []
      const phoneSet = new Set<string>()
      let duplicates = 0
      let invalidPhone = 0
      let emptyRows = 0

      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) { emptyRows++; continue }

        const vals = lines[i].split(",").map(v => v.trim().replace(/^"|"$/g, ""))
        const row = {} as CsvRow
        headers.forEach((h, idx) => { row[h] = vals[idx] || "" })

        if (!row.name || !row.phone) {
          emptyRows++
          continue
        }

        // Validate phone: digits only, at least 10 chars
        const cleanPhone = row.phone.replace(/\D/g, "")
        if (cleanPhone.length < 10) {
          invalidPhone++
          continue
        }

        if (phoneSet.has(cleanPhone)) {
          duplicates++
          continue
        }

        phoneSet.add(cleanPhone)
        rows.push({ ...row, phone: cleanPhone })
      }

      if (!rows.length) {
        setCsvError("No valid rows found. Ensure names are present and phone numbers are 10+ digits.")
        return
      }

      let errorMsg = ""
      if (invalidPhone > 0) errorMsg += `${invalidPhone} invalid phone(s). `
      if (duplicates > 0) errorMsg += `${duplicates} duplicate(s) removed. `

      if (errorMsg) {
        setCsvError(`Loaded ${rows.length} contacts. Note: ${errorMsg}`)
      }

      setCsvRows(rows)
    }
    reader.readAsText(file)
  }, [])

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith(".csv")) { setCsvError("Only .csv files accepted"); return }
    setCsvFile(file); parseCSV(file)
  }
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragging(true) }
  const onDragLeave = () => setDragging(false)
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragging(false)
    const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f)
  }

  // ── Wizard ─────────────────────────────────────────────────────────────────
  const canNext = () => {
    if (wizardStep === 1) return campaignName.trim().length > 0;
    if (wizardStep === 2) {
      if (csvFile && (csvRows.length === 0 || csvError.includes("Missing columns") || csvError.includes("No valid rows"))) {
        return false;
      }
    }
    return true;
  }

  const resetWizard = () => {
    setWizardStep(1); setCampaignName(""); setDescription("")
    setPriority("medium"); setMaxRetries(3); setCallFrom("09:00"); setCallTo("18:00")
    setCsvFile(null); setCsvRows([]); setCsvError(""); setShowWizard(false)
    setIvrName("XeroAlpha")
  }

  const handleCreate = async () => {
    setCreating(true)
    try {
      const res = await API.post("/auto-dialer/campaigns", {
        name: campaignName.trim(),
        description: description.trim(),
        priority,
        max_retries: maxRetries,
        call_from: callFrom,
        call_to: callTo,
        ivr_rec_name: ivrName.trim()
      })
      const created: Campaign = res.data

      if (csvFile && csvRows.length > 0) {
        const form = new FormData(); form.append("file", csvFile)
        const up = await API.post(`/auto-dialer/campaigns/${created.id}/upload-csv`, form, {
          headers: { "Content-Type": "multipart/form-data" }
        })
        if (up.status === 200) {
          const d = up.data
          toast.success(`Campaign created with ${d.inserted} contacts! 🎉`)
          fetchContacts(created.id)
        } else {
          toast.success("Campaign created!"); toast.error("CSV upload failed — retry later")
        }
      } else { toast.success(`Campaign "${created.name}" created! 🎉`) }

      setCampaigns(prev => [created, ...prev])
      setSelectedCampaignId(created.id)
      resetWizard()
    } catch (e: any) { toast.error(e.message || "Failed") }
    finally { setCreating(false) }
  }

  const handleEdit = async () => {
    if (!editingCampaign || !editName.trim()) return
    setUpdating(true)
    try {
      const res = await API.put(`/auto-dialer/campaigns/${editingCampaign.id}`, {
        name: editName.trim(),
        description: editDesc.trim(),
        priority: editPriority,
        max_retries: editRetries,
        call_from: editFrom,
        call_to: editTo,
        ivr_rec_name: editIvrName.trim()
      })
      toast.success("Campaign updated")
      fetchCampaigns()
      setEditingCampaign(null)
    } catch { toast.error("Update failed") }
    finally { setUpdating(false) }
  }

  const handleDelete = async () => {
    if (!deletingCampaign) return
    setIsDeleting(true)
    try {
      const res = await API.delete(`/auto-dialer/campaigns/${deletingCampaign.id}`)
      toast.success("Campaign deleted")
      setCampaigns(prev => prev.filter(c => c.id !== deletingCampaign.id))
      if (selectedCampaignId === deletingCampaign.id) setSelectedCampaignId("")
      setDeletingCampaign(null)
    } catch { toast.error("Delete failed") }
    finally { setIsDeleting(false) }
  }

  const handleRelaunch = async () => {
    if (!relaunchingCampaign) return
    setIsRelaunching(true)
    try {
      const res = await API.post(`/auto-dialer/campaigns/${relaunchingCampaign.id}/relaunch/`)
      toast.success("Campaign relaunched successfully! 🚀")
      fetchCampaigns()
      setRelaunchingCampaign(null)
    } catch (e: any) {
      toast.error(e.response?.data?.error || "Relaunch failed")
    } finally {
      setIsRelaunching(false)
    }
  }

  // ── Campaign dialer controls ───────────────────────────────────────────────
  const handleStartCampaign = async () => {
    if (!selectedCampaignId) {
      toast.warning("Please select a campaign first from the Campaigns table.")
      return
    }
    try {
      const res = await API.post(`/auto-dialer/campaigns/${selectedCampaignId}/dispatch`)
      const data = res.data
      toast.success(`Campaign dispatched! ${data.queued_contacts} contacts queued.`)
      setElapsed(0)
      fetchCampaigns()
    } catch (e: any) { toast.error(e.message || "Failed to start campaign") }
  }

  const handlePauseCampaign = async () => {
    if (!selectedCampaignId) return
    try {
      await API.patch(`/auto-dialer/campaigns/${selectedCampaignId}/status`, { status: "paused" })
      toast.info("Campaign paused ⏸")
      fetchCampaigns()
    } catch { toast.error("Failed to pause campaign") }
  }

  const handleResumeCampaign = async () => {
    if (!selectedCampaignId) return
    try {
      const res = await API.post(`/auto-dialer/campaigns/${selectedCampaignId}/dispatch`)
      const data = res.data
      toast.success(`Campaign resumed! ${data.queued_contacts} contacts re-queued.`)
      fetchCampaigns()
    } catch (e: any) { toast.error(e.message || "Failed to resume campaign") }
  }

  const [syncing, setSyncing] = useState(false)
  const handleSyncStatus = async () => {
    if (!selectedCampaignId) return
    setSyncing(true)
    try {
      await API.post(`/auto-dialer/campaigns/${selectedCampaignId}/sync_status/`)
      await fetchContacts(selectedCampaignId, true)
      await fetchCampaigns(true)
      toast.success("Statuses synced with vendor")
    } catch (err) {
      console.error("Sync failed", err)
      toast.error("Failed to sync statuses")
    } finally {
      setSyncing(false)
    }
  }

  const handleStopCampaign = async () => {
    if (!selectedCampaignId) return
    try {
      await API.patch(`/auto-dialer/campaigns/${selectedCampaignId}/status`, { status: "active" })
      toast.error("Campaign stopped")
      setElapsed(0)
      fetchCampaigns()
    } catch { toast.error("Failed to stop campaign") }
  }

  // ── Filtered contacts ──────────────────────────────────────────────────────
  const filtered = useMemo(() => contacts.filter(l =>
    (l.name.toLowerCase().includes(search.toLowerCase()) || l.phone.includes(search)) &&
    (statusFilter === "All" || l.status === statusFilter)
  ), [contacts, search, statusFilter])

  const pages = Math.ceil(filtered.length / pageSize) || 1
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize)

  // ── Stats derived from loaded contacts (= 0 until campaign selected) ────────
  const statusBadge = (s: string) => ({
    "Completed": "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20",
    "Initiated": "bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20",
    "Dialing": "bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20",
    "Queued": "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20",
    "Failed": "bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20"
  }[s] || "bg-slate-500 shadow-sm")

  const campaignBadge = (s: string) => ({
    active: "bg-emerald-500 shadow-sm", dispatched: "bg-violet-600 shadow-sm",
    completed: "bg-slate-500 shadow-sm", paused: "bg-amber-500 shadow-sm"
  }[s] || "bg-slate-400 shadow-sm")

  // ── Shared class helpers ───────────────────────────────────────────────────
  const card = "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm"
  const text = "text-slate-900 dark:text-slate-50"
  const muted = "text-slate-500 dark:text-slate-400"
  const input = "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 border-slate-200 dark:border-slate-700 focus:ring-violet-500"
  const row = "hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-200"

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-950 min-h-screen transition-colors duration-300 font-sans">

      {/* Header */}
      <div className={`flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${card} p-5 rounded-2xl shadow-xl backdrop-blur-xl bg-white/70 dark:bg-slate-900/70 sticky top-4 z-50`}>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-violet-600 rounded-xl shadow-lg shadow-violet-500/30 ring-4 ring-violet-500/10">
            <Phone className="text-white" size={24} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              
              
            </div>
            <h1 className={`text-2xl font-bold tracking-tight ${text}`}>Auto-Dialer Control</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
              <p className={`text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400`}>System Operational</p>
            </div>
          </div>
        </div>

        {/* Professional Area: Working Number & Clock */}
        <div className="flex flex-wrap items-center gap-3 lg:gap-6">
          {/* Live Clock */}
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-100 dark:bg-gray-700/50 rounded-xl border border-slate-200 dark:border-gray-700 shadow-inner">
            <div className="flex flex-col items-end leading-none">
              <span className={`text-[10px] uppercase tracking-tighter font-bold ${muted}`}>Current Session</span>
              <span className={`text-sm font-mono font-bold ${text}`}>
                {currentTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <div className="h-8 w-px bg-slate-300 dark:bg-gray-600" />
            <span className="text-xl font-mono font-black tracking-tighter text-purple-600 dark:text-purple-400">
              {currentTime.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>

          {/* Working Number Identification */}
          <div className="flex items-center gap-3 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800/50 shadow-sm transition-all hover:bg-indigo-100 dark:hover:bg-indigo-800/30">
            <div className="p-1.5 bg-indigo-600 rounded-lg text-white shadow-md shadow-indigo-500/20">
              <Activity size={16} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-indigo-500/80">Active Trunk</p>
              <p className={`text-sm font-mono font-bold ${text}`}><span className="text-indigo-600 dark:text-indigo-400">NewLeafGlobal</span></p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            {selectedCampaign?.status === "active" && (
              <Button size="sm" onClick={handleStartCampaign}
                className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-emerald-600 dark:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm transition-all duration-300 hover:scale-105 active:scale-95 font-bold">
                <Play size={15} /> Start
              </Button>
            )}
            {selectedCampaign?.status === "dispatched" && (
              <>
                <Button size="sm" onClick={handlePauseCampaign}
                  className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-violet-600 dark:text-violet-400 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm font-bold">
                  <Pause size={15} /> Pause
                </Button>
                <Button size="sm" onClick={handleStopCampaign}
                  className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-rose-600 dark:text-rose-400 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm font-bold">
                  <Activity size={15} /> Stop
                </Button>
              </>
            )}
            {selectedCampaign?.status === "paused" && (
              <>
                <Button size="sm" onClick={handleResumeCampaign}
                  className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-emerald-600 dark:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm font-bold">
                  <Play size={15} /> Resume
                </Button>
                <Button size="sm" onClick={handleStopCampaign}
                  className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-rose-600 dark:text-rose-400 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm font-bold">
                  <Activity size={15} /> Stop
                </Button>
              </>
            )}
            {selectedCampaignId && (
              <Button size="sm" onClick={handleSyncStatus} disabled={syncing}
                className="bg-violet-600 hover:bg-violet-700 text-white shadow-md shadow-violet-500/20 font-bold gap-2">
                <RefreshCw size={15} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing..." : "Sync Status"}
              </Button>
            )}
          </div>

          {/* Timer */}
          <div className="flex flex-col items-center justify-center min-w-[90px] px-3 py-1.5 bg-slate-950 rounded-xl border border-slate-800 shadow-2xl ring-2 ring-violet-500/20">
            <span className="text-[8px] uppercase font-bold text-slate-500 tracking-[0.2em] mb-0.5">Session</span>
            <div className="font-mono text-lg font-black text-white leading-none">
              {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Card */}
      <div className={`${card} rounded-2xl p-6 shadow-lg bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border-slate-200/50 dark:border-slate-800/50`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-violet-500 animate-pulse" />
            <p className={`text-sm font-bold tracking-tight ${text}`}>
              Current Campaign Velocity
              {(view === "contacts" && selectedCampaign) && <span className={`ml-2 font-medium opacity-60 text-xs`}>— {selectedCampaign.name}</span>}
            </p>
          </div>
          {(view === "contacts" && selectedCampaign) && (
            <Badge variant="outline" className="font-mono text-[10px] border-slate-200 dark:border-slate-700 text-slate-400">
              HASH: {selectedCampaign.id.slice(0, 8)}
            </Badge>
          )}
        </div>
        <div className="w-full bg-slate-200 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden shadow-inner">
          <div className="bg-gradient-to-r from-violet-600 via-indigo-600 to-emerald-500 h-full transition-all duration-1000 ease-out shadow-[0_0_15px_rgba(124,58,237,0.3)]"
            style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between mt-3">
          <p className={`text-sm font-black text-violet-600 dark:text-violet-400`}>
            {progress}% <span className="text-[10px] uppercase tracking-tighter text-slate-400 dark:text-slate-500 font-bold ml-1">Completion</span>
          </p>
          <p className={`text-[11px] font-bold ${muted}`}>
            <span className={text}>{(view === "contacts" && selectedCampaign) ? selectedCampaign.completed_contacts : 0}</span>
            <span className="mx-1">/</span>
            {(view === "contacts" && selectedCampaign) ? selectedCampaign.total_contacts : 0} <span className="opacity-60">Leads Handled</span>
          </p>
        </div>
      </div>

      {/* ─── Stat Tiles ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Total Leads", val: derivedStats.total, accent: "border-indigo-500/20", h: "hover:bg-indigo-500/5 hover:border-indigo-500/50 hover:shadow-indigo-500/10", t: "text-indigo-600 dark:text-indigo-400", icon: Users, sub: "Dataset Size" },
          { label: "Completed", val: derivedStats.completed, accent: "border-emerald-500/20", h: "hover:bg-emerald-500/5 hover:border-emerald-500/50 hover:shadow-emerald-500/10", t: "text-emerald-600 dark:text-emerald-400", icon: CheckCircle2, sub: `${Math.round((derivedStats.completed / (derivedStats.total || 1)) * 100)}% Conversion` },
          { label: "Initiated", val: derivedStats.initiated, accent: "border-blue-500/20", h: "hover:bg-blue-500/5 hover:border-blue-500/50 hover:shadow-blue-500/10", t: "text-blue-600 dark:text-blue-400", icon: Activity, sub: "Active In-Flight" },
          { label: "Queued", val: derivedStats.queued, accent: "border-amber-500/20", h: "hover:bg-amber-500/5 hover:border-amber-500/50 hover:shadow-amber-500/10", t: "text-amber-600 dark:text-amber-400", icon: Clock, sub: "Pending Dial" },
          { label: "Failed", val: derivedStats.failed, accent: "border-rose-500/20", h: "hover:bg-rose-500/5 hover:border-rose-500/50 hover:shadow-rose-500/10", t: "text-rose-600 dark:text-rose-400", icon: AlertCircle, sub: "Dropped / Error" },
        ].map(s => (
          <div key={s.label}
            className={`${card} border ${s.accent} ${s.h} rounded-2xl p-5 transition-all duration-300 group relative overflow-hidden`}>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-[10px] font-bold uppercase tracking-widest ${muted} group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors`}>{s.label}</p>
              <s.icon className={`h-4 w-4 ${muted} opacity-50 group-hover:opacity-100 group-hover:${s.t} transition-all`} />
            </div>
            <div className="flex items-baseline gap-2">
              <p className={`text-3xl font-black tracking-tight ${s.t} group-hover:scale-105 transition-transform origin-left`}>{s.val ?? 0}</p>
            </div>
            <p className="text-[10px] font-medium text-slate-400 dark:text-slate-500 mt-1">{s.sub}</p>
            <div className={`absolute bottom-0 left-0 h-1 bg-current opacity-10 w-full group-hover:opacity-30 transition-opacity ${s.t}`} />
          </div>
        ))}
      </div>

      {/* Enhanced Performance Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign Health Card */}
        <div className={`${card} rounded-2xl p-6 shadow-lg flex flex-col justify-between overflow-hidden relative group`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className={`text-sm font-bold ${text}`}>Campaign Health</h3>
              <p className={`text-[10px] uppercase font-bold text-slate-400 tracking-tight`}>Conversion Quality</p>
            </div>
            <div className={`p-2 rounded-xl bg-emerald-500/10 ${derivedStats.successRate > 70 ? 'text-emerald-500' : 'text-amber-500'}`}>
              <Zap size={20} />
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="relative h-20 w-20 flex items-center justify-center">
              <svg className="h-full w-full transform -rotate-90">
                <circle cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-100 dark:text-slate-800" />
                <circle cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="8" fill="transparent" 
                  strokeDasharray={213} 
                  strokeDashoffset={213 - (213 * derivedStats.successRate) / 100}
                  className="text-emerald-500 transition-all duration-1000 ease-out" 
                />
              </svg>
              <span className={`absolute text-lg font-black ${text}`}>{derivedStats.successRate}%</span>
            </div>
            <div className="space-y-1">
              <p className={`text-2xl font-black ${text}`}>Premium</p>
              <p className={`text-xs font-medium ${muted}`}>Response Rate is {derivedStats.successRate > 50 ? 'Above' : 'Below'} Industry Avg</p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
             <span className="text-[10px] font-bold text-slate-400">SYNCED: {currentTime.toLocaleTimeString()}</span>
             <Badge className="bg-emerald-500/10 text-emerald-600 border-none text-[10px]">REAL-TIME</Badge>
          </div>
        </div>

        {/* Batch Efficiency Card */}
        <div className={`${card} rounded-2xl p-6 shadow-lg flex flex-col`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-bold ${text}`}>Batch Efficiency</h3>
            <Layers className="text-violet-500" size={18} />
          </div>
          <div className="space-y-4 flex-1">
             <div className="flex justify-between items-end">
                <div>
                   <p className="text-2xl font-black text-violet-600 dark:text-violet-400">5,000</p>
                   <p className="text-[10px] font-bold text-slate-400 uppercase">Leads Per Bundle</p>
                </div>
                <div className="text-right">
                   <p className={`text-sm font-bold ${text}`}>{Math.ceil(derivedStats.total / 5000)} Batches</p>
                   <p className="text-[10px] font-bold text-slate-400 uppercase">Total Chunks</p>
                </div>
             </div>
             <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between text-[10px] font-bold mb-1.5">
                   <span className="text-violet-500">CURRENT PROGRESS</span>
                   <span className={text}>{Math.min(100, Math.round((derivedStats.completed / 5000) * 100))}%</span>
                </div>
                <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                   <div className="h-full bg-violet-500" style={{ width: `${Math.min(100, (derivedStats.completed / 5000) * 100)}%` }} />
                </div>
             </div>
          </div>
          <p className="text-[10px] font-medium text-slate-400 mt-4 italic">Sequential processing ensures line stability.</p>
        </div>

        {/* System Load / Capacity */}
        <div className={`${card} rounded-2xl p-6 shadow-lg flex flex-col`}>
           <div className="flex items-center justify-between mb-4">
             <h3 className={`text-sm font-bold ${text}`}>System Capacity</h3>
             <Activity className="text-blue-500" size={18} />
           </div>
           <div className="grid grid-cols-2 gap-4 flex-1">
              <div className="p-4 bg-blue-500/5 rounded-2xl border border-blue-500/10">
                 <p className="text-xl font-black text-blue-600 dark:text-blue-400">2</p>
                 <p className="text-[10px] font-bold text-slate-400 uppercase">Active IVRs</p>
              </div>
              <div className="p-4 bg-indigo-500/5 rounded-2xl border border-indigo-500/10">
                 <p className="text-xl font-black text-indigo-600 dark:text-indigo-400">1.0s</p>
                 <p className="text-[10px] font-bold text-slate-400 uppercase">Dial Latency</p>
              </div>
           </div>
           <div className="mt-4 p-3 bg-emerald-500/10 rounded-xl flex items-center gap-3">
              <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
              <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-tighter">Cluster Health: Optimized for 50K+</p>
           </div>
        </div>
      </div>



      {/* ─── Campaigns Table ─────────────────────────────────────────────── */}
      {view === "campaigns" && (
        <div className={`${card} rounded-xl shadow-sm overflow-hidden`}>
          <div className="px-5 py-4 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between">
            <p className={`text-sm font-semibold ${text}`}>All Auto-Dialer Campaigns</p>
            <Button size="sm" onClick={() => setShowWizard(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2 px-4 shadow-md shadow-violet-500/20 rounded-xl font-bold">
              <Plus size={16} /> Create Campaign
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-gray-700/50">
                {["Name", "Campaign ID", "Status", "Contacts", "Completed", "Action"].map(h => (
                  <TableHead key={h} className={`text-xs font-semibold ${muted}`}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {!campaigns.length && (
                <TableRow><TableCell colSpan={6} className={`text-center py-10 ${muted}`}>
                  No campaigns yet — click "Create Campaign" to get started.
                </TableCell></TableRow>
              )}
              {campaigns.map(c => (
                <TableRow key={c.id}
                  className={`${row} ${c.id === selectedCampaignId ? "bg-violet-50 dark:bg-violet-900/10 border-l-4 border-violet-500" : ""}`}>
                  <TableCell>
                    <button
                      onClick={() => {
                        setSelectedCampaignId(c.id);
                        fetchContacts(c.id);
                        setView("contacts");
                        setPage(1);
                      }}
                      className={`font-bold hover:text-violet-600 dark:hover:text-violet-400 transition-colors cursor-pointer text-left ${text}`}
                    >
                      {c.name}
                    </button>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-400 dark:text-gray-500">{c.id}</TableCell>
                  <TableCell><Badge className={`${campaignBadge(c.status)} text-white text-xs`}>{c.status}</Badge></TableCell>
                  <TableCell className={muted}>{c.total_contacts}</TableCell>
                  <TableCell className={muted}>{c.completed_contacts}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setRelaunchingCampaign(c)}
                        className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded transition"
                        title="Relaunch / Restart">
                        <RefreshCw size={15} />
                      </button>
                      <button
                        onClick={() => {
                          setEditingCampaign(c);
                          setEditName(c.name);
                          setEditDesc(c.description || "");
                          setEditPriority((c.priority as any) || "medium");
                          setEditRetries(c.max_retries || 3);
                          setEditFrom(c.call_from || "09:00");
                          setEditTo(c.call_to || "18:00");
                          setEditIvrName(c.ivr_rec_name || "XeroAlpha");
                        }}
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
                        title="Edit">
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => setDeletingCampaign(c)}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                        title="Delete">
                        <Trash2 size={15} />
                      </button>
                      <button
                        onClick={() => {
                          const url = `${API.defaults.baseURL}/auto-dialer/campaigns/${c.id}/report/`;
                          window.open(url, '_blank');
                        }}
                        className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition"
                        title="Download CSV Report">
                        <FileText size={15} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* ─── Contacts Table ───────────────────────────────────────────────── */}
      {view === "contacts" && (<>
        {/* Filters & Navigation */}
        <div className={`flex justify-between items-center ${card} p-3 rounded-xl shadow-sm space-x-4`}>
          <div className="flex gap-3 items-center">
            <Button variant="outline" size="sm" onClick={() => setView("campaigns")}
              className="flex items-center gap-1 text-slate-600 dark:text-gray-400">
              <ArrowLeft size={14} /> Back to Campaigns
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-gray-700 mx-1" />
            <Input placeholder="Search name or phone" value={search}
              onChange={e => setSearch(e.target.value)}
              className={`max-w-xs ${input}`} />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className={`border rounded-lg px-3 py-2 text-sm ${input} focus:outline-none focus:ring-2`}>
              {["All", "Queued", "Initiated", "Dialing", "Completed", "Failed"].map(s =>
                <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold uppercase tracking-widest ${muted}`}>Daily Quota</span>
            <div className="w-28 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden shadow-inner">
              <div className="h-full bg-violet-600 dark:bg-violet-500 shadow-[0_0_8px_rgba(124,58,237,0.4)]" style={{ width: "60%" }} />
            </div>
            <span className={`text-xs font-black ${text}`}>60 / 100</span>
          </div>
        </div>

        {/* Table */}
        <div className={`${card} rounded-xl shadow-sm overflow-hidden`}>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-gray-700/50">
                {["#", "Name", "Phone", "Status", "Attempts", "Recording"].map(h => (
                  <TableHead key={h} className={`text-xs font-semibold ${muted}`}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {!paginated.length && (
                <TableRow><TableCell colSpan={6} className={`text-center py-10 ${muted}`}>
                  {!campaigns.length ? 'No campaigns — click "Create Campaign" above.' : "No contacts. Upload CSV when creating a campaign."}
                </TableCell></TableRow>
              )}
              {paginated.map(c => (
                <TableRow key={c.id} className={row}>
                  <TableCell className={muted}>{c.serial_number}</TableCell>
                  <TableCell className={`font-medium ${text}`}>{c.name}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-600 dark:text-gray-300">{c.phone}</TableCell>
                  <TableCell><Badge className={`${statusBadge(c.status)} text-white text-xs`}>{c.status}</Badge></TableCell>
                  <TableCell className={`font-semibold ${text}`}>{c.attempts}</TableCell>
                  <TableCell>
                    {c.recording_url
                      ? <button onClick={() => setPlayer(c.recording_url || null)}
                        className="flex items-center gap-1 px-3 py-1 text-xs font-medium border border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition">
                        <Headphones size={13} /> Listen
                      </button>
                      : <button
                        onClick={() => toast.info("No recording available for this contact yet.")}
                        className="flex items-center justify-center p-1.5 text-xs font-medium border border-border text-muted-foreground rounded-md cursor-not-allowed"
                        title="No recording">
                        <Play size={14} />
                      </button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
            className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium bg-card text-muted-foreground border border-border hover:bg-muted disabled:opacity-40 transition`}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span className={`text-sm text-muted-foreground`}>Page {page} / {pages}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page === pages}
            className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium bg-card text-muted-foreground border border-border hover:bg-muted disabled:opacity-40 transition`}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </>)}

      {/* ══════════════════════ CREATE CAMPAIGN ══════════════════════ */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden border-border/80 shadow-[0_30px_70px_rgba(0,0,0,0.4)] dark:shadow-[0_40px_80px_rgba(0,0,0,0.6)] bg-background [&>button]:text-white [&>button]:opacity-100 [&>button]:top-5 [&>button]:right-6">
          <div className="flex flex-col h-[600px] max-h-[80vh]">
            {/* Wizard header */}
            <div className="bg-violet-600 px-6 py-5 text-white flex items-center justify-between shadow-lg shrink-0">
              <div>
                <DialogTitle className="text-xl font-black tracking-tight flex items-center gap-2 text-white">
                  <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-md">
                    <Plus size={20} className="text-white" />
                  </div>
                  New Campaign Engine
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-violet-200 text-[10px] font-bold uppercase tracking-widest">
                    Optimization Phase {wizardStep} / {STEPS.length}
                  </span>
                  <span className="h-1 w-1 rounded-full bg-violet-400" />
                  <span className="text-white/80 text-[10px] font-semibold">{STEPS[wizardStep - 1].label}</span>
                </div>
              </div>
            </div>

            {/* Step bar */}
            <div className="flex items-center px-8 py-4 bg-muted/30 border-b border-border shrink-0">
              {STEPS.map((step, idx) => {
                const Icon = step.icon
                const done = wizardStep > step.id
                const active = wizardStep === step.id
                return (
                  <React.Fragment key={step.id}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-sm font-bold transition-all duration-300 ${done ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                        : active ? "bg-violet-600 text-white shadow-lg shadow-violet-500/30 ring-4 ring-violet-500/10"
                          : "bg-card text-muted-foreground border border-border"}`}>
                        {done ? <CheckCircle2 size={20} /> : <Icon size={18} />}
                      </div>
                      <div className="hidden lg:block">
                        <p className={`text-[10px] font-bold uppercase tracking-widest leading-none mb-0.5 ${active ? "text-violet-600 dark:text-violet-400" : "text-muted-foreground"}`}>Step {step.id}</p>
                        <p className={`text-xs font-bold ${active ? "text-foreground" : "text-muted-foreground"}`}>
                          {step.label}
                        </p>
                      </div>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className={`flex-1 h-px mx-4 ${wizardStep > step.id ? "bg-emerald-400" : "bg-border"}`} />
                    )}
                  </React.Fragment>
                )
              })}
            </div>

            {/* Body */}
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-8 space-y-6">
                {/* ─ Step 1 ─ */}
                {wizardStep === 1 && (<>
                  <div>
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>Campaign Name <span className="text-red-500">*</span></label>
                    <input value={campaignName} onChange={e => setCampaignName(e.target.value)} autoFocus
                      placeholder="e.g. Q2 Sales Outreach…"
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`} />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>Description / Notes</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)}
                      placeholder="Optional — describe the campaign goal…" rows={3}
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`} />
                  </div>


                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 text-muted-foreground`}>Priority Matrix</label>
                    <div className="flex gap-2">
                      {PRIORITIES.map(p => (
                        <button key={p.value} onClick={() => setPriority(p.value as any)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-tighter border-2 transition-all duration-200 ${priority === p.value
                            ? `${p.color} text-white border-transparent shadow-lg scale-[1.02]`
                            : "bg-background text-muted-foreground border-border hover:border-violet-500/50"}`}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {[["Call Hours From", callFrom, setCallFrom], ["Call Hours To", callTo, setCallTo]].map(([lbl, val, set]) => (
                      <div key={lbl as string}>
                        <label className={`block text-sm font-semibold mb-1 flex items-center gap-1 text-foreground`}>
                          <Clock size={14} /> {lbl as string}
                        </label>
                        <input type="time" value={val as string} onChange={e => (set as any)(e.target.value)}
                          className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`} />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-1 flex items-center gap-1 text-foreground`}>
                      <RefreshCw size={14} /> Max Retries per Contact
                    </label>
                    <div className="flex items-center gap-3">
                      <input type="range" min={1} max={5} value={maxRetries}
                        onChange={e => setMaxRetries(Number(e.target.value))}
                        className="flex-1 accent-violet-600" />
                      <span className="w-8 text-center font-black text-violet-600 dark:text-violet-400 text-lg">{maxRetries}</span>
                    </div>
                    <p className={`text-xs mt-1 text-muted-foreground`}>Each contact retried up to {maxRetries}x if not connected.</p>
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>
                      IVR Name / Assistant <span className="text-red-500">*</span>
                    </label>
                    {assistants.length > 0 ? (
                      <select 
                        value={ivrName} 
                        onChange={e => setIvrName(e.target.value)}
                        className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`}
                      >
                        <option value="">Select Assistant</option>
                        {assistants.map(a => (
                          <option key={a.id} value={a.id}>{a.name || a.id}</option>
                        ))}
                        {!assistants.find(a => a.id === "XeroAlpha") && (
                          <option value="XeroAlpha">XeroAlpha (Default)</option>
                        )}
                      </select>
                    ) : (
                      <input value={ivrName} onChange={e => setIvrName(e.target.value)}
                        placeholder="e.g. NLGSHU, etc."
                        className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`} />
                    )}
                    <p className={`text-[10px] mt-1 text-muted-foreground uppercase tracking-widest font-bold`}>
                      This maps to the "ivr" parameter in the XeroAlpha API
                    </p>
                  </div>
                </>)}

                {/* ─ Step 2 ─ */}
                {wizardStep === 2 && (<>
                  <div
                    onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${dragging ? "border-violet-500 bg-violet-500/5 scale-[1.01]"
                      : csvError ? "border-red-400 bg-red-500/5"
                        : csvFile ? "border-green-400 bg-green-500/5"
                          : "border-border hover:border-violet-400 hover:bg-violet-500/5"}`}>
                    <input ref={fileInputRef} type="file" accept=".csv" className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f) }} />
                    {csvFile && !csvError ? (
                      <div className="space-y-1">
                        <CheckCircle2 size={36} className="mx-auto text-green-500" />
                        <p className="font-semibold text-green-600 dark:text-green-400">{csvFile.name}</p>
                        <p className="text-sm text-green-600/70">{csvRows.length} valid contacts found</p>
                        <p className={`text-xs text-muted-foreground`}>Click to replace</p>
                      </div>
                    ) : csvError ? (
                      <div className="space-y-1">
                        <AlertCircle size={36} className="mx-auto text-red-500" />
                        <p className="font-semibold text-red-600 dark:text-red-400">Invalid CSV</p>
                        <p className="text-sm text-red-500">{csvError}</p>
                        <p className={`text-xs text-muted-foreground`}>Click to try another file</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload size={36} className={`mx-auto text-muted-foreground`} />
                        <p className={`font-semibold text-foreground`}>Drag & drop your CSV here</p>
                        <p className={`text-sm text-muted-foreground`}>or click to browse</p>
                        <p className={`text-xs text-muted-foreground`}>
                          Required: <code className="bg-muted px-1 rounded">name</code> and{" "}
                          <code className="bg-muted px-1 rounded">phone</code>
                        </p>
                      </div>
                    )}
                  </div>

                  {csvRows.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-semibold flex items-center gap-1 text-foreground`}>
                          <FileText size={14} /> Preview (first 5 of {csvRows.length})
                        </p>
                        <Badge className="bg-green-500 text-white border-none">{csvRows.length} contacts</Badge>
                      </div>
                      <div className="border border-border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className={`text-xs text-muted-foreground`}>#</TableHead>
                              <TableHead className={`text-xs text-muted-foreground`}>Name</TableHead>
                              <TableHead className={`text-xs text-muted-foreground`}>Phone</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {csvRows.slice(0, 5).map((r, i) => (
                              <TableRow key={i} className="hover:bg-muted/30 transition-colors">
                                <TableCell className={`text-xs text-muted-foreground`}>{i + 1}</TableCell>
                                <TableCell className={`text-sm text-foreground`}>{r.name}</TableCell>
                                <TableCell className="text-sm font-mono text-muted-foreground">{r.phone}</TableCell>
                              </TableRow>
                            ))}
                            {csvRows.length > 5 && (
                              <TableRow><TableCell colSpan={3} className={`text-center text-xs py-2 text-muted-foreground`}>
                                … and {csvRows.length - 5} more contacts
                              </TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  {!csvFile && <p className={`text-xs text-center text-muted-foreground`}>CSV is optional — upload contacts later from the campaigns table.</p>}
                </>)}

                {/* ─ Step 3 ─ */}
                {wizardStep === 3 && (
                  <div className="space-y-4">
                    <p className={`text-sm text-muted-foreground`}>Review your campaign before creating.</p>
                    <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-5 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className={`text-lg font-bold text-foreground`}>{campaignName}</p>
                          {description && <p className={`text-sm mt-0.5 text-muted-foreground`}>{description}</p>}
                        </div>
                        <Badge className={`${PRIORITIES.find(p => p.value === priority)?.color} text-white border-none`}>
                          {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-3 pt-1">
                        {[
                          { icon: Clock, label: "Call Hours", value: `${callFrom} – ${callTo}` },
                          { icon: RefreshCw, label: "Max Retries", value: `${maxRetries}x per contact` },
                          { icon: Users, label: "Contacts", value: csvRows.length > 0 ? `${csvRows.length} loaded` : "None yet" },
                        ].map(s => (
                          <div key={s.label} className="bg-background rounded-lg p-3 text-center border border-border shadow-sm">
                            <s.icon size={16} className="mx-auto text-violet-500 mb-1" />
                            <p className={`text-xs text-muted-foreground`}>{s.label}</p>
                            <p className={`text-sm font-semibold text-foreground`}>{s.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {csvFile && csvRows.length > 0 ? (
                      <div className="flex items-center gap-3 bg-green-500/5 border border-green-500/20 rounded-lg p-3">
                        <CheckCircle2 size={18} className="text-green-500 shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-green-600 dark:text-green-400">{csvFile.name}</p>
                          <p className="text-xs text-green-500/70">{csvRows.length} contacts ready to import</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 bg-muted border border-border rounded-lg p-3 text-muted-foreground">
                        <AlertCircle size={18} className="shrink-0" />
                        <p className={`text-sm font-medium`}>No CSV uploaded — contacts can be added later.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Footer - Premium sticky effect */}
            <div className="px-8 py-5 border-t border-border bg-background/80 backdrop-blur-md flex items-center justify-between shrink-0 shadow-[0_-8px_20px_rgba(0,0,0,0.03)] dark:shadow-[0_-8px_20px_rgba(0,0,0,0.2)]">
              <button
                onClick={() => wizardStep > 1 ? setWizardStep(s => s - 1) : resetWizard()}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border transition
                  ${wizardStep === 1
                    ? "border-red-500 text-red-500 hover:bg-red-500/10"
                    : "border-border text-foreground hover:bg-muted"
                  }`}
              >
                {wizardStep === 1 ? <X size={15} /> : <ArrowLeft size={15} />}
                {wizardStep === 1 ? "Cancel" : "Back"}
              </button>

              {wizardStep < 3 ? (
                <button onClick={() => setWizardStep(s => s + 1)} disabled={!canNext()}
                  className="flex items-center gap-1 px-8 py-2.5 rounded-xl text-sm font-black uppercase tracking-tight bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40 transition shadow-lg shadow-violet-500/30">
                  Next Phase <ArrowRight size={15} />
                </button>
              ) : (
                <button onClick={handleCreate} disabled={creating}
                  className="flex items-center gap-2 px-8 py-2.5 rounded-xl text-sm font-black uppercase tracking-tight bg-emerald-600 hover:bg-emerald-700 text-white min-w-[170px] justify-center transition shadow-lg shadow-emerald-500/30">
                  {creating ? <><Loader2 size={15} className="animate-spin" /> Provisioning…</> : <><CheckCircle2 size={15} /> Deploy Campaign</>}
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Audio Player ────────────────────────────────────────────────── */}
      <Dialog open={!!player} onOpenChange={(open) => !open && setPlayer(null)}>
        <DialogContent className="max-w-sm p-0 overflow-hidden border-border bg-background shadow-2xl dark:shadow-[var(--shadow-premium)] animate-in slide-in-from-bottom-4 duration-300">
          <div className="flex flex-col">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-muted/30">
              <DialogTitle className="flex items-center gap-2 text-base font-bold">
                <Headphones size={18} className="text-violet-600" />
                Audio Intelligence Review
              </DialogTitle>
              {dialerRunning && (
                <span className="flex items-center gap-1 text-[10px] uppercase font-bold text-emerald-500 tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Playing
                </span>
              )}
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-muted px-4 py-6 rounded-xl border border-border shadow-inner">
                <audio
                  ref={audioRef}
                  controls
                  src={player || ""}
                  className="w-full"
                  onEnded={() => { setElapsed(0) }}
                  onTimeUpdate={() => { if (audioRef.current) setElapsed(Math.floor(audioRef.current.currentTime)) }}
                />
              </div>
              <Button onClick={() => {
                const a = audioRef.current; if (a) { a.pause(); a.currentTime = 0 }
                setElapsed(0); setPlayer(null)
              }}
                variant="outline"
                className="w-full h-11 rounded-xl">
                Finish Review
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Edit Modal ─────────────────────────────────────────────────── */}
      <Dialog open={!!editingCampaign} onOpenChange={(open) => !open && setEditingCampaign(null)}>
        <DialogContent className="max-w-xl p-0 overflow-hidden border-border bg-background shadow-2xl dark:shadow-[var(--shadow-premium)] animate-in zoom-in-95 duration-200">
          <div className="flex flex-col">
            <div className="bg-background px-6 py-5 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-500/10 rounded-xl text-violet-500">
                  <Pencil size={20} />
                </div>
                <DialogTitle className="text-xl font-black tracking-tight">Refine Strategy</DialogTitle>
              </div>
            </div>

            <ScrollArea className="max-h-[60vh]">
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1 md:col-span-2">
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>Campaign Name</label>
                    <input
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>Description</label>
                    <textarea
                      value={editDesc}
                      onChange={e => setEditDesc(e.target.value)}
                      rows={2}
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>Priority</label>
                    <select
                      value={editPriority}
                      onChange={e => setEditPriority(e.target.value as any)}
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`}
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>Max Retry Attempts</label>
                    <input
                      type="number"
                      min="1" max="10"
                      value={editRetries}
                      onChange={e => setEditRetries(parseInt(e.target.value) || 1)}
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>Call Start Time</label>
                    <input
                      type="time"
                      value={editFrom}
                      onChange={e => setEditFrom(e.target.value)}
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>Call End Time</label>
                    <input
                      type="time"
                      value={editTo}
                      onChange={e => setEditTo(e.target.value)}
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <label className={`block text-sm font-semibold mb-1 text-foreground`}>IVR Name / Assistant</label>
                    <input
                      value={editIvrName}
                      onChange={e => setEditIvrName(e.target.value)}
                      className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-background text-foreground`}
                    />
                  </div>
                </div>
              </div>
            </ScrollArea>

            <div className="p-6 pt-0 flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl h-11"
                onClick={() => setEditingCampaign(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-11 rounded-xl font-black bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-500/30 border-none"
                onClick={handleEdit}
                disabled={updating || !editName.trim()}
              >
                {updating ? "Optimizing..." : "Update Engine"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Delete Confirmation Modal ────────────────────────────────────── */}
      <Dialog open={!!deletingCampaign} onOpenChange={(open) => !open && setDeletingCampaign(null)}>
        <DialogContent className="max-w-sm p-0 overflow-hidden border-border bg-background shadow-2xl dark:shadow-[var(--shadow-premium)] animate-in fade-in zoom-in-95 duration-200">
          <div className="p-8 text-center space-y-6">
            <div className="mx-auto w-20 h-20 bg-rose-500/10 rounded-full flex items-center justify-center text-rose-500 shadow-inner">
              <Trash2 size={36} />
            </div>
            <div>
              <DialogTitle className="text-2xl font-black tracking-tight text-foreground">Destructive Action</DialogTitle>
              <p className={`text-sm mt-2 px-2 font-medium leading-relaxed text-muted-foreground`}>
                Permanently terminate <span className="font-bold text-rose-500 underline decoration-rose-500/30 underline-offset-4 font-mono">"{deletingCampaign?.name}"</span>?
                This will purge all associated data and records.
              </p>
            </div>
            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                className="flex-1 h-11 rounded-xl font-bold"
                onClick={() => setDeletingCampaign(null)}
              >
                Retreat
              </Button>
              <Button
                className="flex-1 h-11 rounded-xl font-black bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-500/30 border-none"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Purging..." : "Eliminate"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Relaunch Confirmation Modal ────────────────────────────────────── */}
      <Dialog open={!!relaunchingCampaign} onOpenChange={(open) => !open && setRelaunchingCampaign(null)}>
        <DialogContent className="max-w-sm p-0 overflow-hidden border-border bg-background shadow-2xl dark:shadow-[var(--shadow-premium)] animate-in fade-in zoom-in-95 duration-200">
          <div className="p-8 text-center space-y-6">
            <div className="mx-auto w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center text-emerald-500 shadow-inner">
              <RefreshCw size={36} className={isRelaunching ? "animate-spin" : ""} />
            </div>
            <div>
              <DialogTitle className="text-2xl font-black tracking-tight text-foreground">Relaunch Engine</DialogTitle>
              <p className={`text-sm mt-2 px-2 font-medium leading-relaxed text-muted-foreground`}>
                This will reset ALL contacts in <span className="font-bold text-emerald-500 underline decoration-emerald-500/30 underline-offset-4 font-mono">"{relaunchingCampaign?.name}"</span> and start the dialing sequence from scratch.
              </p>
            </div>
            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                className="flex-1 h-11 rounded-xl font-bold"
                onClick={() => setRelaunchingCampaign(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-11 rounded-xl font-black bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/30 border-none"
                onClick={handleRelaunch}
                disabled={isRelaunching}
              >
                {isRelaunching ? "Relaunching..." : "Relaunch"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  )
}
